import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  makeStyles,
  IconButton,
  Snackbar,
  CircularProgress,
  Chip,
  Paper,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import LockIcon from '@material-ui/icons/Lock';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useApi, discoveryApiRef, identityApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { argoCDApiRef } from '@roadiehq/backstage-plugin-argo-cd';
import { useArgocdConfig } from '../argocd-enhanced/hooks/useArgocdConfig';

const useStyles = makeStyles(theme => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    height: 'calc(100vh - 300px)',
    overflow: 'auto',
    paddingRight: theme.spacing(1),
  },
  resultBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all',
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
  },
  copyButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  clusterInfo: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(3),
  },
  chip: {
    height: 28,
  },
  controllerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  successIcon: {
    color: theme.palette.success.main,
  },
  keyValueRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  keyValuePaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  addButton: {
    marginTop: theme.spacing(1),
  },
  keyValueIndex: {
    minWidth: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
  resultArea: {
    height: 'calc(100vh - 300px)',
    overflow: 'auto',
    paddingRight: theme.spacing(1),
  },
  yamlBox: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    wordBreak: 'break-all',
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
  },
}));

interface ClusterInfo {
  clusterName: string;
  server: string;
  backstageClusterName: string;
}

interface ControllerInfo {
  controllerName: string;
  controllerNamespace: string;
  serviceName: string;
  version?: string;
}

interface KeyValuePair {
  key: string;
  value: string;
  encryptedValue?: string;
  loading?: boolean;
  error?: string;
}

export const SealedSecretTool: React.FC = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const argoApi = useApi(argoCDApiRef);
  const { entity } = useEntity();
  const { instances } = useArgocdConfig();

  const [clusterInfo, setClusterInfo] = useState<ClusterInfo | null>(null);
  const [controllerInfo, setControllerInfo] = useState<ControllerInfo | null>(null);
  const [clusterLoading, setClusterLoading] = useState(true);
  const [controllerLoading, setControllerLoading] = useState(false);
  const [clusterError, setClusterError] = useState<string>('');
  const [controllerError, setControllerError] = useState<string>('');

  const [secretName, setSecretName] = useState('');
  const [secretNamespace, setSecretNamespace] = useState('');
  const [keyValuePairs, setKeyValuePairs] = useState<KeyValuePair[]>([{ key: '', value: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const annotations = entity?.metadata?.annotations || {};
  const argoCDAppName = annotations['argocd/app-name'];
  const argoCDAppNamespace = annotations['argocd/app-namespace'] || 'argocd';
  const argoCDInstanceName = instances?.[0]?.name || 'default';

  // 解析集群名称
  const resolveCluster = useCallback(async (
    destinationName: string
  ): Promise<{ clusterName: string; serverUrl: string } | null> => {
    try {
      const proxyUrl = await discoveryApi.getBaseUrl('scaffolder');
      const { token } = await identityApi.getCredentials();

      const response = await fetch(
        `${proxyUrl}/k8s-cluster-resolver/resolve?destinationName=${encodeURIComponent(destinationName)}`,
        { headers: { ...(token && { Authorization: `Bearer ${token}` }) } }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.clusterName) {
          return { clusterName: data.clusterName, serverUrl: data.serverUrl };
        }
      }
      return null;
    } catch {
      return null;
    }
  }, [discoveryApi, identityApi]);

  // 检测 SealedSecret Controller
  const checkSealedSecretController = useCallback(async (backstageClusterName: string) => {
    setControllerLoading(true);
    setControllerError('');
    setControllerInfo(null);

    try {
      const baseUrl = await discoveryApi.getBaseUrl('sealed-secret-api');
      const response = await fetchApi.fetch(
        `${baseUrl}/detect-controller?clusterName=${encodeURIComponent(backstageClusterName)}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `检测失败: ${response.status}`);
      }

      const controller = await response.json();
      setControllerInfo(controller);
    } catch (err) {
      setControllerError(err instanceof Error ? err.message : '检测 SealedSecret Controller 失败');
    } finally {
      setControllerLoading(false);
    }
  }, [discoveryApi, fetchApi]);

  // 获取 ArgoCD 目标集群
  useEffect(() => {
    let isMounted = true;

    const fetchClusterInfo = async () => {
      if (!argoCDAppName) {
        setClusterError('未配置 argocd/app-name 注解');
        setClusterLoading(false);
        return;
      }

      setClusterLoading(true);
      try {
        const appDetails = await argoApi.getAppDetails({
          url: `/argoInstance/${argoCDInstanceName}`,
          appName: argoCDAppName,
          appNamespace: argoCDAppNamespace,
          instance: argoCDInstanceName,
        });

        if (!appDetails) {
          throw new Error(`ArgoCD Application ${argoCDAppName} not found`);
        }

        const appAny = appDetails as any;
        const destName = appAny.spec?.destination?.name || '';
        const destServer = appAny.spec?.destination?.server || '';
        const namespace = appAny.spec?.destination?.namespace || 'default';

        let backstageClusterName: string | null = null;
        let serverUrl = destServer;

        if (destName) {
          const resolved = await resolveCluster(destName);
          if (resolved) {
            backstageClusterName = resolved.clusterName;
            serverUrl = resolved.serverUrl || destServer;
          }
        }

        if (!backstageClusterName && destServer) {
          const resolved = await resolveCluster(destServer);
          if (resolved) {
            backstageClusterName = resolved.clusterName;
          }
        }

        if (!backstageClusterName) {
          if (isMounted) {
            setClusterError(`无法在 Backstage 配置中找到匹配的集群: ${destName || destServer}`);
            setClusterLoading(false);
          }
          return;
        }

        if (isMounted) {
          setClusterInfo({
            clusterName: destName || backstageClusterName,
            server: serverUrl,
            backstageClusterName,
          });
          setSecretNamespace(namespace);
          setClusterLoading(false);
          
          checkSealedSecretController(backstageClusterName);
        }
      } catch (err) {
        if (isMounted) {
          setClusterError(`获取 ArgoCD 应用失败: ${err}`);
          setClusterLoading(false);
        }
      }
    };

    fetchClusterInfo();

    return () => {
      isMounted = false;
    };
  }, [argoCDAppName, argoCDAppNamespace, argoCDInstanceName, argoApi, resolveCluster, checkSealedSecretController]);

  const addKeyValuePair = () => {
    setKeyValuePairs([...keyValuePairs, { key: '', value: '' }]);
  };

  const removeKeyValuePair = (index: number) => {
    if (keyValuePairs.length > 1) {
      setKeyValuePairs(keyValuePairs.filter((_, i) => i !== index));
    }
  };

  const updateKeyValuePair = (index: number, field: 'key' | 'value', newValue: string) => {
    const updated = [...keyValuePairs];
    updated[index] = { ...updated[index], [field]: newValue };
    setKeyValuePairs(updated);
  };

  const handleEncrypt = async () => {
    if (!secretName || !secretNamespace) {
      setError('请填写 Secret 名称和命名空间');
      return;
    }

    const validPairs = keyValuePairs.filter(p => p.key && p.value);
    if (validPairs.length === 0) {
      setError('请至少填写一个 Key-Value 对');
      return;
    }

    if (!controllerInfo || !clusterInfo) {
      setError('未检测到 SealedSecret Controller');
      return;
    }

    setLoading(true);
    setError(null);

    // 重置所有加密结果
    setKeyValuePairs(prev => prev.map(p => ({ ...p, encryptedValue: undefined, error: undefined })));

    const baseUrl = await discoveryApi.getBaseUrl('sealed-secret-api');

    // 并行加密所有 key-value 对
    const results = await Promise.all(
      keyValuePairs.map(async (pair, index) => {
        if (!pair.key || !pair.value) {
          return { index, encryptedValue: undefined, error: undefined };
        }

        try {
          const response = await fetchApi.fetch(`${baseUrl}/encrypt`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              secretName,
              secretNamespace,
              secretValue: pair.value,
              clusterName: clusterInfo.backstageClusterName,
              controllerNamespace: controllerInfo.controllerNamespace,
              serviceName: controllerInfo.serviceName,
            }),
          });

          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || '加密失败');
          }

          const data = await response.json();
          return { index, encryptedValue: data.encryptedValue, error: undefined };
        } catch (err) {
          return { index, encryptedValue: undefined, error: err instanceof Error ? err.message : '加密失败' };
        }
      })
    );

    // 更新状态
    setKeyValuePairs(prev => {
      const updated = [...prev];
      results.forEach(result => {
        updated[result.index] = {
          ...updated[result.index],
          encryptedValue: result.encryptedValue,
          error: result.error,
        };
      });
      return updated;
    });

    setLoading(false);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbarOpen(true);
  };

  const generateYaml = () => {
    const encryptedPairs = keyValuePairs.filter(p => p.key && p.encryptedValue);
    if (encryptedPairs.length === 0) return '';

    const encryptedDataLines = encryptedPairs
      .map(p => `    ${p.key}: ${p.encryptedValue}`)
      .join('\n');

    return `apiVersion: bitnami.com/v1alpha1
kind: SealedSecret
metadata:
  name: ${secretName}
  namespace: ${secretNamespace}
spec:
  encryptedData:
${encryptedDataLines}`;
  };

  const hasEncryptedResults = keyValuePairs.some(p => p.encryptedValue);

  if (clusterLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={300}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 8 }}>
          正在获取集群信息...
        </Typography>
      </Box>
    );
  }

  if (clusterError) {
    return (
      <Box>
        <Alert severity="error">{clusterError}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {clusterInfo && (
        <Box className={classes.clusterInfo}>
          <Chip
            className={classes.chip}
            label={`集群名: ${clusterInfo.clusterName}`}
            variant="outlined"
            size="small"
          />
          <Chip
            className={classes.chip}
            label={`Server: ${clusterInfo.server}`}
            variant="outlined"
            size="small"
          />
        </Box>
      )}

      {controllerLoading && (
        <Box className={classes.controllerInfo}>
          <CircularProgress size={16} />
          <Typography variant="body2">正在检测 SealedSecret Controller...</Typography>
        </Box>
      )}

      {controllerError && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {controllerError}
        </Alert>
      )}

      {controllerInfo && (
        <Box className={classes.controllerInfo}>
          <CheckCircleIcon className={classes.successIcon} fontSize="small" />
          <Typography variant="body2">
            Controller: {controllerInfo.controllerNamespace}/{controllerInfo.controllerName}
            {controllerInfo.version && ` (${controllerInfo.version})`}
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Box className={classes.form}>
            <TextField
              label="Secret 名称"
              variant="outlined"
              size="small"
              fullWidth
              required
              value={secretName}
              onChange={(e) => setSecretName(e.target.value)}
              placeholder="my-secret"
            />
            <TextField
              label="Secret 命名空间"
              variant="outlined"
              size="small"
              fullWidth
              required
              value={secretNamespace}
              onChange={(e) => setSecretNamespace(e.target.value)}
              placeholder="default"
            />

            <Typography variant="subtitle2" style={{ marginTop: 8 }}>
              Key-Value 对
            </Typography>

            {keyValuePairs.map((pair, index) => (
              <Paper key={index} className={classes.keyValuePaper} variant="outlined">
                <Box className={classes.keyValueRow}>
                  <Box className={classes.keyValueIndex}>{index + 1}</Box>
                  <Box flex={1}>
                    <TextField
                      label="Key"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={pair.key}
                      onChange={(e) => updateKeyValuePair(index, 'key', e.target.value)}
                      placeholder="password"
                      style={{ marginBottom: 8 }}
                    />
                    <TextField
                      label="Value (明文)"
                      variant="outlined"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={pair.value}
                      onChange={(e) => updateKeyValuePair(index, 'value', e.target.value)}
                      placeholder="输入需要加密的敏感数据"
                    />
                    {pair.error && (
                      <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                        {pair.error}
                      </Typography>
                    )}
                    {pair.encryptedValue && (
                      <Typography variant="caption" color="primary" style={{ marginTop: 4, display: 'block' }}>
                        ✓ 已加密
                      </Typography>
                    )}
                  </Box>
                  {keyValuePairs.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeKeyValuePair(index)}
                      title="删除"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={addKeyValuePair}
              className={classes.addButton}
            >
              添加 Key-Value
            </Button>

            <Button
              variant="contained"
              color="primary"
              startIcon={<LockIcon />}
              onClick={handleEncrypt}
              disabled={loading || !controllerInfo}
              style={{ marginTop: 16 }}
            >
              {loading ? '加密中...' : '加密全部'}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={7}>
          {error && (
            <Alert severity="error" style={{ marginBottom: 16 }}>
              {error}
            </Alert>
          )}

          {hasEncryptedResults ? (
            <Box className={classes.resultArea}>
              <Typography variant="subtitle2" gutterBottom>
                加密结果:
              </Typography>
              {keyValuePairs.filter(p => p.key && p.encryptedValue).map((pair, index) => (
                <Box key={index} style={{ marginBottom: 16 }}>
                  <Typography variant="caption" color="textSecondary">
                    {pair.key}:
                  </Typography>
                  <Box className={classes.resultBox}>
                    <IconButton
                      className={classes.copyButton}
                      size="small"
                      onClick={() => handleCopy(pair.encryptedValue!)}
                      title="复制"
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', paddingRight: 32 }}>
                      {pair.encryptedValue}
                    </pre>
                  </Box>
                </Box>
              ))}

              <Typography variant="subtitle2" gutterBottom style={{ marginTop: 24 }}>
                完整 SealedSecret YAML:
              </Typography>
              <Box className={classes.yamlBox}>
                <IconButton
                  className={classes.copyButton}
                  size="small"
                  onClick={() => handleCopy(generateYaml())}
                  title="复制 YAML"
                >
                  <FileCopyIcon fontSize="small" />
                </IconButton>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', paddingRight: 32 }}>
                  {generateYaml()}
                </pre>
              </Box>
            </Box>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="100%"
              minHeight={300}
              style={{ backgroundColor: '#f5f5f5', borderRadius: 4 }}
            >
              <Typography variant="body2" color="textSecondary">
                填写左侧表单并点击"加密全部"按钮
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        message="已复制到剪贴板"
      />
    </Box>
  );
};

export default SealedSecretTool;
